import { createTheme } from '@mui/material/styles';

// ─── Design Tokens ─────────────────────────────────────────────────────────────
// Decisión tipográfica: Plus Jakarta Sans.
// - Tiene carácter humanista sin ser decorativa. Legible en tablas densas.
// - Contrasta con la "blandura" de Inter/Roboto. Transmite precisión técnica.
// - JetBrains Mono para IDs, versiones y datos técnicos: código y datos tienen
//   diferente ritmo visual que el texto UI, esta separación es deliberada.

export const tokens = {
  color: {
    // Brand — teal más oscuro y serio que el original, menos "startup"
    brand: {
      50:  '#E6F3F3',
      100: '#C0E2E2',
      200: '#8BCBCB',
      300: '#55B3B3',
      400: '#25A0A0',
      500: '#0A7A7A', // primary — usamos este, no el #0a8181 original
      600: '#086B6B',
      700: '#065757',
      800: '#044444',
      900: '#023232',
    },
    ink: {
      primary:   '#18293A', // slate navy — no puro negro, no #2c3e50 cliché
      secondary: '#445566', // mid blue-gray para text secundario
      muted:     '#7A8A97', // captions, placeholders — solo en texto grande (AA)
      inverse:   '#FFFFFF',
    },
    surface: {
      default: '#F0EDE7', // parchment cálido — NO #f5f7fa frío genérico
      paper:   '#FFFFFF',
      subtle:  '#E8E4DC', // headers de tabla, fondos de código
      overlay: 'rgba(24, 41, 58, 0.04)', // hover rows
    },
    border: {
      default: '#D4D0C8', // warm border
      strong:  '#AAA69C', // divisores de tabla, bordes de énfasis
      focus:   '#0A7A7A', // focus ring
    },
    // Status — colores semánticos, verificados WCAG AA
    status: {
      active:    { text: '#145E33', bg: '#EBF9F1', border: '#AEEACB' },
      suspended: { text: '#92400E', bg: '#FFFBEB', border: '#FDE68A' },
      offline:   { text: '#991B1B', bg: '#FEF2F2', border: '#FECACA' },
      pending:   { text: '#3730A3', bg: '#EEF2FF', border: '#C7D2FE' },
    },
    semantic: {
      success: '#15803D',
      warning: '#D97706',
      error:   '#B91C1C',
      info:    '#1D4ED8',
    },
  },
  font: {
    sans: '"Plus Jakarta Sans", system-ui, -apple-system, sans-serif',
    mono: '"JetBrains Mono", "Fira Code", ui-monospace, monospace',
  },
  radius: {
    sm:   '4px',
    md:   '8px',
    lg:   '12px',
    xl:   '16px',
    full: '9999px',
  },
  shadow: {
    xs:    '0 1px 2px rgba(24,41,58,0.06)',
    sm:    '0 1px 4px rgba(24,41,58,0.08), 0 1px 2px rgba(24,41,58,0.05)',
    md:    '0 4px 12px rgba(24,41,58,0.08), 0 2px 4px rgba(24,41,58,0.05)',
    lg:    '0 8px 28px rgba(24,41,58,0.10), 0 4px 10px rgba(24,41,58,0.06)',
    focus: '0 0 0 3px rgba(10,122,122,0.22)',
  },
};

// ─── MUI Theme ─────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: {
      main:         tokens.color.brand[500],
      light:        tokens.color.brand[400],
      dark:         tokens.color.brand[700],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         tokens.color.ink.secondary,
      contrastText: '#FFFFFF',
    },
    background: {
      default: tokens.color.surface.default,
      paper:   tokens.color.surface.paper,
    },
    text: {
      primary:   tokens.color.ink.primary,
      secondary: tokens.color.ink.secondary,
      disabled:  tokens.color.ink.muted,
    },
    success: { main: tokens.color.semantic.success, contrastText: '#fff' },
    warning: { main: tokens.color.semantic.warning, contrastText: '#fff' },
    error:   { main: tokens.color.semantic.error,   contrastText: '#fff' },
    info:    { main: tokens.color.semantic.info,    contrastText: '#fff' },
    divider: tokens.color.border.default,
  },

  typography: {
    fontFamily: tokens.font.sans,
    h1: { fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.2,  color: tokens.color.ink.primary },
    h2: { fontWeight: 700, fontSize: '1.625rem', lineHeight: 1.25, color: tokens.color.ink.primary },
    h3: { fontWeight: 600, fontSize: '1.375rem', lineHeight: 1.3,  color: tokens.color.ink.primary },
    h4: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.35, color: tokens.color.ink.primary },
    h5: { fontWeight: 600, fontSize: '1rem',     lineHeight: 1.4,  color: tokens.color.ink.primary },
    h6: { fontWeight: 600, fontSize: '0.9375rem',lineHeight: 1.45, color: tokens.color.ink.primary },
    subtitle1: { fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.5, color: tokens.color.ink.primary },
    subtitle2: { fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.5, color: tokens.color.ink.secondary },
    body1:     { fontSize: '0.9375rem', lineHeight: 1.55, color: tokens.color.ink.primary },
    body2:     { fontSize: '0.8125rem', lineHeight: 1.5,  color: tokens.color.ink.secondary },
    caption:   { fontSize: '0.75rem',   lineHeight: 1.5,  color: tokens.color.ink.muted, letterSpacing: '0.01em' },
    overline:  { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: tokens.color.ink.muted },
  },

  shape: { borderRadius: 8 },

  // Shadows simplificadas — solo 3 niveles reales
  shadows: [
    'none',
    tokens.shadow.xs,
    tokens.shadow.sm,
    tokens.shadow.sm,
    tokens.shadow.md,
    tokens.shadow.md,
    tokens.shadow.md,
    tokens.shadow.md,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
    tokens.shadow.lg,
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': { boxSizing: 'border-box' },
        body: {
          backgroundColor: tokens.color.surface.default,
          color: tokens.color.ink.primary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        // Scrollbar discreta
        '::-webkit-scrollbar': { width: '6px', height: '6px' },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        '::-webkit-scrollbar-thumb': {
          background: tokens.color.border.strong,
          borderRadius: '3px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: tokens.color.ink.muted,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          borderRadius: tokens.radius.md,
          padding: '7px 16px',
          letterSpacing: '0',
          transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
          '&:focus-visible': {
            outline: 'none',
            boxShadow: tokens.shadow.focus,
          },
        },
        sizeSmall: { padding: '4px 10px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '10px 22px', fontSize: '0.9375rem' },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none', filter: 'brightness(0.93)' },
          '&:active': { filter: 'brightness(0.87)' },
          '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
        },
        outlined: {
          borderColor: tokens.color.border.default,
          '&:hover': {
            borderColor: tokens.color.border.strong,
            backgroundColor: tokens.color.surface.overlay,
          },
        },
        text: {
          '&:hover': { backgroundColor: tokens.color.surface.overlay },
        },
        containedError: {
          backgroundColor: tokens.color.semantic.error,
          '&:hover': { backgroundColor: tokens.color.semantic.error, filter: 'brightness(0.9)' },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: tokens.color.surface.overlay },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: tokens.shadow.focus,
          },
        },
        sizeSmall: { padding: '5px' },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${tokens.color.border.default}`,
          borderRadius: tokens.radius.lg,
          boxShadow: 'none',
          backgroundColor: tokens.color.surface.paper,
          // Sin hover-lift — las tarjetas son contenedores, no acciones
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation0: { border: `1px solid ${tokens.color.border.default}`, boxShadow: 'none' },
        elevation1: { border: 'none', boxShadow: tokens.shadow.sm },
        elevation2: { border: 'none', boxShadow: tokens.shadow.md },
        elevation8: { border: 'none', boxShadow: tokens.shadow.lg },
        elevation16: { border: 'none', boxShadow: tokens.shadow.lg },
        elevation24: { border: 'none', boxShadow: tokens.shadow.lg },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small', variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            borderRadius: tokens.radius.md,
            backgroundColor: tokens.color.surface.paper,
            transition: 'box-shadow 0.15s',
            '& fieldset': {
              borderColor: tokens.color.border.default,
              transition: 'border-color 0.15s',
            },
            '&:hover fieldset': { borderColor: tokens.color.border.strong },
            '&.Mui-focused fieldset': {
              borderColor: tokens.color.brand[500],
              borderWidth: '1.5px',
            },
            '&.Mui-focused': { boxShadow: tokens.shadow.focus },
            '&.Mui-error fieldset': { borderColor: tokens.color.semantic.error },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            color: tokens.color.ink.secondary,
            '&.Mui-focused': { color: tokens.color.brand[600] },
            '&.Mui-error': { color: tokens.color.semantic.error },
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.75rem',
            marginLeft: 0,
          },
        },
      },
    },

    MuiSelect: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        icon: { color: tokens.color.ink.muted },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.75rem',
          height: '22px',
          borderRadius: tokens.radius.sm,
          letterSpacing: '0',
        },
        sizeSmall: { height: '20px', fontSize: '0.7rem' },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          fontSize: '0.875rem',
          padding: '8px 14px',
          alignItems: 'center',
          border: '1px solid transparent',
        },
        standardError: {
          backgroundColor: tokens.color.status.offline.bg,
          color: tokens.color.status.offline.text,
          borderColor: tokens.color.status.offline.border,
          '& .MuiAlert-icon': { color: tokens.color.status.offline.text },
        },
        standardSuccess: {
          backgroundColor: tokens.color.status.active.bg,
          color: tokens.color.status.active.text,
          borderColor: tokens.color.status.active.border,
          '& .MuiAlert-icon': { color: tokens.color.status.active.text },
        },
        standardWarning: {
          backgroundColor: tokens.color.status.suspended.bg,
          color: tokens.color.status.suspended.text,
          borderColor: tokens.color.status.suspended.border,
          '& .MuiAlert-icon': { color: tokens.color.status.suspended.text },
        },
        standardInfo: {
          backgroundColor: '#EFF6FF',
          color: tokens.color.semantic.info,
          borderColor: '#BFDBFE',
          '& .MuiAlert-icon': { color: tokens.color.semantic.info },
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.color.border.default },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.xl,
          border: 'none',
          boxShadow: tokens.shadow.lg,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 12px',
          fontSize: '1rem',
          fontWeight: 600,
          color: tokens.color.ink.primary,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '4px 24px 16px' },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: '8px',
          '& > :not(:first-of-type)': { marginLeft: 0 },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: '42px',
          padding: '8px 16px',
          color: tokens.color.ink.secondary,
          '&.Mui-selected': {
            color: tokens.color.brand[500],
            fontWeight: 600,
          },
          '&:focus-visible': { boxShadow: tokens.shadow.focus },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '42px',
          borderBottom: `1px solid ${tokens.color.border.default}`,
        },
        indicator: {
          height: '2px',
          borderRadius: '2px',
          backgroundColor: tokens.color.brand[500],
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.full,
          height: '5px',
          backgroundColor: tokens.color.surface.subtle,
        },
        bar: { borderRadius: tokens.radius.full },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.color.ink.primary,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: tokens.radius.sm,
          padding: '4px 8px',
        },
        arrow: { color: tokens.color.ink.primary },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          padding: '7px 10px',
          marginBottom: '2px',
          '&:hover': { backgroundColor: tokens.color.surface.overlay },
          '&.Mui-selected': {
            backgroundColor: tokens.color.brand[50],
            '& .MuiListItemIcon-root': { color: tokens.color.brand[500] },
            '& .MuiListItemText-primary': {
              color: tokens.color.brand[600],
              fontWeight: 600,
            },
          },
          '&.Mui-selected:hover': {
            backgroundColor: tokens.color.brand[100],
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '34px',
          color: tokens.color.ink.muted,
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem',
          fontWeight: 500,
          color: tokens.color.ink.secondary,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: tokens.color.surface.subtle,
            color: tokens.color.ink.muted,
            fontWeight: 600,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            borderBottom: `1px solid ${tokens.color.border.default}`,
            padding: '10px 16px',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokens.color.border.default}`,
          padding: '11px 16px',
          fontSize: '0.875rem',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: tokens.color.surface.overlay },
          '&:last-child td': { borderBottom: 'none' },
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '7px 14px',
          borderRadius: tokens.radius.sm,
          margin: '1px 4px',
          minHeight: 'unset',
          '&:hover': { backgroundColor: tokens.color.surface.overlay },
          '&.Mui-selected': {
            backgroundColor: tokens.color.brand[50],
            color: tokens.color.brand[600],
            fontWeight: 600,
          },
        },
      },
    },

    MuiInputAdornment: {
      styleOverrides: {
        root: { color: tokens.color.ink.muted },
      },
    },
  },
});

export default theme;
